import moment from 'moment';
import DateFormats from '@/core/constants/DateFormats';

class Quarter {
  /**
   * Get the quarter identifier for the previous quarter
   * @returns {string} The identifier for the previous quarter (e.g. "202501" for Q1 of 2025)
   */
  static getPreviousQuarterCode() {
    const threeMonthsAgo = moment().subtract(3, 'months');
    const quarter = threeMonthsAgo.quarter();
    const year = threeMonthsAgo.year();

    return `${year}0${quarter}`;
  }
  /**
   * Returns the dates from the beginning of the current quarter up to the current date
   * @param {string} [outputFormat='DD-MM-YYYY'] - Optional format for the returned date strings
   * @returns {{startDate: moment.Moment, endDate: moment.Moment, startDateFormatted: string, endDateFormatted: string}} An object containing the start and end dates as moment objects and formatted strings
   */
  static getCurrentQuarterToDateRange(outputFormat = DateFormats.ISO_DATE) {
    const today = moment();
    const currentQuarter = today.quarter();
    const startDate = moment().quarter(currentQuarter).startOf('quarter');
    const endDate = today;
    return {
      startDate,
      endDate,
      startDateFormatted: startDate.format(outputFormat),
      endDateFormatted: endDate.format(outputFormat),
    };
  }
  /**
   *
   * Return quarter label in a readable format
   *
   * @param {(string|number)} quarter - Quarter identifier.
   * Accepts either:
   *  - Two-digit string: '01', '02', '03', '04'
   *  - Numeric value: 1, 2, 3, 4
   *
   * @returns {string} Label of the period corresponding to the quarter. Ex: Enero - Febrero - Marzo
   *
   * Returns an empty string if the value is not valid.
   */
  static getPeriodLabel(quarter) {
    if (typeof quarter === 'number' && !Number.isNaN(quarter)) {
      quarter = `0${quarter}`;
    }
    const labels = {
      '01': 'Ene - Feb - Mar',
      '02': 'Abr - May - Jun',
      '03': 'Jul - Ago - Sept',
      '04': 'Oct - Nov - Dic',
    };

    return labels[quarter] ?? '';
  }

  /**
   * Parses a period value in YYYYQQ format and extracts year and quarter.
   *
   * @param {(string|number)} fullQuarter - Period identifier in YYYYQQ format.
   * Examples:
   *  - '202401'
   *  - 202402
   *
   * @returns {Object} Parsed period data.
   * @returns {string} returns.year - Year extracted from the period (YYYY).
   * @returns {string} returns.quarter - Quarter extracted from the period ('01'–'04').
   */

  static parse(fullQuarter) {
    const value = fullQuarter.toString();

    const quarter = value.slice(-2);
    const year = value.slice(0, 4);

    return {
      quarter,
      year,
    };
  }

  /**
   * Returns the last date of the current quarter
   * @param {string} [outputFormat='DD [de] MMMM YYYY'] format for the return date
   * @returns {{endDate: moment.Moment, endDateFormatted: string}} contain the enda date of current quarter, in date format and string format
   *
   */
  static getCurrentQuarterEndDate(outputFormat = 'D [de] MMMM [de] YYYY') {
    const endDate = moment().endOf('quarter');
    return {
      endDate,
      endDateFormatted: endDate.format(outputFormat),
    };
  }

  /**
   * Returns the name of the first and last month of quarter
   *
   * @param {(string|number)} quarter - Quarter identifier.
   * Acepts:
   *  - Two digit string: '01', '02', '03', '04'
   *  - Numeric value: 1, 2, 3, 4
   * @param {number} [year=moment().year()] - current year is optional
   *
   * @returns {string} Month range label for the quarter. Example: "April - Jun"
   */
  static getQuarterMonthRangeLabel(quarter, year = moment().year()) {
    const q = typeof quarter === 'string' ? parseInt(quarter, 10) : quarter;

    if (!q || q < 1 || q > 4) {
      return '';
    }

    const startMonthIndex = (q - 1) * 3;
    const endMonthIndex = startMonthIndex + 2;

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    const startMonth = capitalize(
      moment().year(year).month(startMonthIndex).format('MMMM')
    );
    const endMonth = capitalize(
      moment().year(year).month(endMonthIndex).format('MMMM')
    );

    return `${startMonth} - ${endMonth}`;
  }

  /**
   * Returns the month range (first and last month) of the current quarter
   * Example: "April - Jun"
   *
   * @returns {string} Month range label of the current quarter
   */
  static getCurrentQuarterMonthRangeLabel() {
    const today = moment();
    return this.getQuarterMonthRangeLabel(today.quarter(), today.year());
  }

  /**
   * Returns the month range (first and last month) of the previous quarter
   *
   * @returns {string} Month range label of the previous quarter
   */
  static getPreviousQuarterMonthRangeLabel() {
    const previousQuarterCode = this.getPreviousQuarterCode();
    const { year, quarter } = this.parse(previousQuarterCode);

    return this.getQuarterMonthRangeLabel(quarter, Number(year));
  }
}

export const quarterInstance = new Quarter();
export default Quarter;
